import type { ReactNode } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';

export interface SelectionBannerProps {
  /**
   * Human-readable selection summary.
   *
   * The caller decides the wording, e.g.:
   * - "Выбрано товаров: 5"
   * - "Выбраны все 120 товаров в результатах поиска"
   */
  label: string;
  /**
   * Optional extra controls placed to the left of the "clear" button.
   * Use for bulk-operation triggers (e.g. "Update stock", "Archive selected").
   */
  actions?: ReactNode;
  /** Label for the clear button. Defaults to "Очистить". */
  clearLabel?: string;
  /** Called when the user clicks the clear button. */
  onClear: () => void;
}

/**
 * Compact selection-state banner for data tables with bulk actions.
 *
 * Renders when one or more rows are selected. Shows a summary label and
 * provides a clear button. Use the `actions` slot for bulk-operation triggers.
 *
 * ──────────────────────────────────────────────────────────────────
 * Usage pattern:
 *
 *   {selectedCount > 0 && (
 *     <SelectionBanner
 *       label={`Выбрано: ${selectedCount}`}
 *       onClear={() => setSelected(new Set())}
 *       actions={
 *         <Button size="small" onClick={openBulkDialog}>
 *           Обновить наличие
 *         </Button>
 *       }
 *     />
 *   )}
 *
 * ──────────────────────────────────────────────────────────────────
 */
export function SelectionBanner({
  label,
  actions,
  clearLabel = 'Очистить',
  onClear,
}: SelectionBannerProps) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        px: 1.5,
        py: 1,
        mb: 1,
        bgcolor: 'action.selected',
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
          {actions}
        </Stack>
        <Button size="small" onClick={onClear}>
          {clearLabel}
        </Button>
      </Stack>
    </Box>
  );
}
